import React, { createContext, useContext, useMemo } from 'react';

import type { LensDataset, LensGovernedMetadata } from '~/types/leLens';

type DisplayMetadata = { savedDatasets?: Array<LensDataset>; metadata?: LensGovernedMetadata };
type Field = { name: string; label?: string; description?: string; kind?: string; type?: string };

const Context = createContext<DisplayMetadata>({});

export function GovernedDisplayMetadataProvider({
  children,
  savedDatasets,
  metadata,
}: DisplayMetadata & { children: React.ReactNode }) {
  const value = useMemo(() => ({ savedDatasets, metadata }), [savedDatasets, metadata]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export const useGovernedDisplayMetadata = () => useContext(Context);

/** A display copy only. Keep raw fields in editor callbacks, requests and query definitions. */
export function governedDisplayField<T extends Field>(
  field: T,
  dataset: LensDataset | undefined,
  { savedDatasets, metadata }: DisplayMetadata,
): T {
  if (!dataset || dataset.source?.kind !== 'governed' || dataset.override) return field;
  const saved = savedDatasets?.find((item) => item.id === dataset.id);
  if (
    !saved ||
    saved.source?.kind !== dataset.source.kind ||
    saved.source.catalogueId !== dataset.source.catalogueId ||
    saved.source.table !== dataset.source.table ||
    saved.source.version !== dataset.source.version ||
    dataset.fields?.some((item) => item.name === field.name)
  )
    return field;
  const original = saved.columns?.find((item) => item.name === field.name);
  if (
    !original ||
    !['dimension', 'date'].includes(original.kind) ||
    original.kind !== field.kind ||
    original.type !== field.type ||
    original.label !== field.label ||
    original.description !== field.description
  )
    return field;
  const display = metadata?.datasets
    .find((item) => item.id === dataset.id)
    ?.columns.find((item) => item.name === field.name);
  if (!display) return field;
  return {
    ...field,
    ...(Object.prototype.hasOwnProperty.call(display, 'label') ? { label: display.label } : {}),
    ...(Object.prototype.hasOwnProperty.call(display, 'description') ? { description: display.description } : {}),
  };
}

export function governedDisplayDataset(dataset: LensDataset | undefined, metadata: DisplayMetadata) {
  return (
    dataset && { ...dataset, columns: dataset.columns?.map((field) => governedDisplayField(field, dataset, metadata)) }
  );
}
